/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/no-array-index-key */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Link,
  Portal,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useMemo, useRef, useState } from 'react';
import { Copy, X } from 'react-feather';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import createHotelNameAnonymizer from 'modules/common/utils/anonymize-hotel-names';
import {
  selectHotelDropdownList,
  selectIsDemoModeActivated,
  selectIsDemoUser,
} from '../../../../selectors';
import { ALERT_TYPES } from '../constants';
import { convertToMarkdown, isSemanticType } from '../functions';
import { selectAlertRemarks } from '../selectors';
/**
 * Side drawer component that displays audit notification details and remarks.
 * @param {boolean} props.show - Controls visibility of the drawer.
 * @param {Function} props.setShowNotification - Callback to close the drawer.
 * @param {Object} props.notification - Notification payload containing audit data.
 * @returns {JSX.Element}
 */
const LogDrawerView = ({ show, setShowNotification, notification }) => {
  const isHorizon = notification?.type === ALERT_TYPES.horizon_audit_vs_stlp.typeName;
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const isDemoUser = useSelector(selectIsDemoUser);
  const hotelList = useSelector(selectHotelDropdownList);
  const remarks = useSelector(selectAlertRemarks);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);
  // Replaces the actual hotel names in the remarks with the demo mode hotel labels
  const anonymize = useMemo(
    () =>
      isDemoUser || isDemoModeActivated ? createHotelNameAnonymizer(hotelList, isDemoUser) : null,
    [hotelList, isDemoUser, isDemoModeActivated]
  );
  const scrub = (text) => (anonymize ? anonymize(text) : text);
  //
  const mdComponents = {
    // Paragraphs
    p: (props) => (
      <p
        style={{
          paddingTop: 12,
          margin: 0,
          lineHeight: 1.65,
          fontSize: 14,
          whiteSpace: 'pre-wrap',
        }}
        {...props}
      />
    ),

    // Headings (## converted markdown)
    h2: (props) => (
      <h2
        style={{
          marginTop: 24,
          marginBottom: 10,
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1.3,
        }}
        {...props}
      />
    ),

    h3: (props) => (
      <h3
        style={{
          marginTop: 18,
          marginBottom: 8,
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1.3,
        }}
        {...props}
      />
    ),

    // Lists
    ul: (props) => (
      <ul
        style={{
          paddingLeft: 20,
          marginTop: 8,
          marginBottom: 8,
          lineHeight: 1.6,
          listStyleType: 'disc',
        }}
        {...props}
      />
    ),

    li: (props) => (
      <li
        style={{
          marginBottom: 4,
          lineHeight: 1.6,
        }}
        {...props}
      />
    ),

    // Table wrapper
    table: (props) => (
      <div style={{ overflowX: 'auto', marginTop: 12, marginBottom: 12 }}>
        <table
          style={{
            width: 'auto',
            borderCollapse: 'collapse',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 13,
          }}
          {...props}
        />
      </div>
    ),

    th: (props) => (
      <th
        style={{
          border: '1px solid #d1d5db',
          padding: '8px 12px',
          textAlign: 'left',
          fontWeight: 600,
          background: '#f9fafb',
          whiteSpace: 'pre-wrap',
        }}
        {...props}
      />
    ),

    td: (props) => (
      <td
        style={{
          border: '1px solid #d1d5db',
          padding: '8px 12px',
          textAlign: 'left',
          whiteSpace: 'pre-wrap',
        }}
        {...props}
      />
    ),
  };
  //
  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!contentRef.current) return;
    try {
      const title = notification?.type ?? '';
      const plainText = `${title}\n====================\n\n${contentRef.current.innerText}`;
      // MUI styles are class based, so inline table borders for the pasted HTML
      const clone = contentRef.current.cloneNode(true);
      clone.querySelectorAll('table').forEach((el) => {
        el.style.borderCollapse = 'collapse';
      });
      clone.querySelectorAll('th, td').forEach((el) => {
        el.style.border = '1px solid #d1d5db';
        el.style.padding = '6px 10px';
      });
      const html = `
        <div style="font-family: Lexend, sans-serif; font-size: 1rem; font-weight: 600; color: #111827; margin-bottom: 4px;">
          <strong>${title}</strong>
        </div>
        <br />
        <div style="font-family: Lexend, sans-serif; font-size: 1rem; font-weight: normal; color: #374151;">
          ${clone.innerHTML}
        </div>`;
      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      });
      await navigator.clipboard.write([clipboardItem]);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy audit details: ', err?.message);
    }
  };
  //
  return (
    <Grid
      xs={12}
      md={4}
      ml={4}
      mt={10}
      item
      sx={{
        flexShrink: 0,
        display: show ? 'block' : 'none',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'white',
          p: 2,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          '& > *': { flexShrink: 0 },
        }}
      >
        {notification && (
          <Grid container direction="row" justifyContent="space-between" alignItems="center" p={4}>
            <Typography variant="h4" color="text.secondary">
              {notification?.type}
            </Typography>
            <Box display="flex" alignItems="center">
              <Tooltip title="Copy Report">
                <span style={{ display: 'inline-block' }}>
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    disabled={remarks === null}
                    sx={{ mr: 1 }}
                  >
                    <Copy size={16} />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton color="inherit" size="small" onClick={() => setShowNotification(false)}>
                <X />
              </IconButton>
            </Box>
          </Grid>
        )}
        {remarks === null ? (
          <Box m={4} display="flex" justifyContent="center" />
        ) : (
          <Box ref={contentRef} width="100%">
            {!isSemanticType(notification?.type) &&
              Array.isArray(remarks) &&
              remarks.length === 0 && (
                <Box m={4}>
                  <Typography variant="body2" color="text.secondary">
                    No remarks available.
                  </Typography>
                </Box>
              )}
            {!isSemanticType(notification?.type) &&
              Array.isArray(remarks) &&
              remarks.map((remark, i) => (
                <Box pl={2} mb={4} width="100%" key={`${remark.hotelName}-${i}`}>
                  {/* Horizon Briefing */}
                  {isHorizon && (
                    <Grid mr={2} ml={2}>
                      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                        {scrub(remark?.hotelName)}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ mb: 6 }}>
                        {scrub(remark.headerText)}
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table
                          sx={{
                            borderCollapse: 'collapse',
                            '& th, & td': {
                              border: '1px solid #e0e0e0',
                              py: 2,
                            },
                          }}
                        >
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f6f6f6' }}>
                              {remark?.tableHeader?.map((col, index) => {
                                const parts = scrub(col).split('<br>');
                                return (
                                  <TableCell
                                    key={`${col}-${index}`}
                                    sx={{ fontSize: 15, fontWeight: 600, textAlign: 'center' }}
                                  >
                                    {parts?.map((part, ind) => (
                                      <span key={ind}>
                                        {part}
                                        {ind !== parts.length - 1 && <br />}
                                      </span>
                                    ))}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {remark.rows?.map((row, ind) => (
                              <TableRow key={`${row?.date?.value}-${ind}`} hover>
                                <TableCell>
                                  <Link href={row?.date?.url} target="_blank" sx={{ mx: 'auto' }}>
                                    <Typography variant="subtitle1">{row?.date?.value}</Typography>
                                  </Link>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle1" textAlign="right">
                                    {`${row.percentageChange} (${row.targetValue}, ${row.comparisonTargetValue})`}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Typography variant="h6" textAlign="left" mt={8} mb={4}>
                        Methodology
                      </Typography>
                      <Typography sx={{ whiteSpace: 'pre-line' }} variant="body1">
                        {scrub(remark?.methodology?.replaceAll('<br>', '\n'))}
                      </Typography>
                    </Grid>
                  )}

                  {/* pace-in-brief audit */}
                  {!isHorizon && !isSemanticType(notification?.type) && (
                    <Grid mr={2} ml={2}>
                      {remark?.hotels && (
                        <>
                          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                            {scrub(remark?.hotels?.join(', '))}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ mb: 4 }}>
                            {scrub(remark?.headerText)}
                          </Typography>
                        </>
                      )}

                      {/* Rolling Basis */}
                      {remark?.rollingBasis?.enabled && (
                        <TableContainerComponent data={remark?.rollingBasis} scrub={scrub} />
                      )}

                      {/* Calendar Basis */}
                      {remark?.calendarBasis?.enabled && (
                        <TableContainerComponent data={remark?.calendarBasis} scrub={scrub} />
                      )}

                      <Typography variant="h6" textAlign="left" mt={8} mb={4}>
                        Methodology
                      </Typography>
                      <Typography sx={{ whiteSpace: 'pre-line' }} variant="body1">
                        {scrub(remark?.methodology?.replaceAll('<br>', '\n'))}
                      </Typography>
                    </Grid>
                  )}
                </Box>
              ))}
            {isSemanticType(notification?.type) && (
              <Grid ml={4}>
                <ReactMarkdown
                  remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                  components={mdComponents}
                >
                  {scrub(convertToMarkdown(remarks?.result))}
                </ReactMarkdown>
                {remarks?.methodology && (
                  <>
                    <Typography variant="h6" textAlign="left" mt={8} mb={4}>
                      Methodology
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-line' }} variant="body1">
                      {scrub(remarks?.methodology?.replaceAll('<br>', '\n'))}
                    </Typography>
                  </>
                )}
              </Grid>
            )}
          </Box>
        )}
      </Box>
      <Portal>
        <Snackbar
          autoHideDuration={3000}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={copied}
          onClose={() => setCopied(false)}
        >
          <Alert severity="success">Report copied to clipboard!</Alert>
        </Snackbar>
      </Portal>
    </Grid>
  );
};
//
export default LogDrawerView;
//
const TableContainerComponent = ({ data, scrub }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ mb: 6 }}>
    <Table
      sx={{
        borderCollapse: 'collapse',
        '& th, & td': {
          border: '1px solid #e0e0e0',
          py: 2,
        },
      }}
    >
      <TableHead>
        <TableRow sx={{ backgroundColor: '#f6f6f6' }}>
          {data?.columns?.map((col, indx) => {
            const parts = scrub(col).split('<br>');
            return (
              <TableCell
                key={`${col}-${indx}`}
                sx={{ fontSize: 15, fontWeight: 600, textAlign: 'center' }}
              >
                {parts?.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i !== parts.length - 1 && <br />}
                  </span>
                ))}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {data?.rows.map((row) => (
          <TableRow key={row?.daysAhead?.value || row?.month?.value} hover>
            <TableCell>
              <Link
                href={row?.daysAhead?.url || row?.month?.url}
                target="_blank"
                sx={{ mx: 'auto' }}
              >
                <Typography variant="subtitle1">
                  {row?.daysAhead?.value || row?.month?.value}
                </Typography>
              </Link>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1" textAlign="right">
                {row?.percentChange?.value}
              </Typography>
            </TableCell>
            {row?.outlook?.value && (
              <TableCell>
                <Typography variant="subtitle1">{scrub(row?.outlook?.value)}</Typography>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);
